import * as React from 'react';

import { UserIcon } from '../../../assets/icons/user/user-icon';
import { cn } from '../../../lib/utils';

export interface NameChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** EP / assigned physician display name from the API */
  name?: string | null;
  /** Shown when `name` is empty or whitespace only (default em dash) */
  emptyLabel?: string;
}

/**
 * Compact chip for displaying a name with user icon (e.g. EP column in patient tables).
 * Uses theme tokens for light/dark compatibility.
 */
export const NameChip = React.forwardRef<HTMLDivElement, NameChipProps>(
  function NameChip({ name, emptyLabel = '—', className, ...props }, ref) {
    const trimmed = name?.trim();
    const hasName = Boolean(trimmed);

    return (
      <div
        ref={ref}
        className={cn(
          'flex min-h-[3.25rem] w-full min-w-0 items-center gap-1',
          className,
        )}
        {...props}
      >
        {hasName ? (
          <div className="flex max-w-full min-w-0 items-center gap-1 rounded-md border border-border bg-muted px-1.5 py-1 sm:px-2">
            <UserIcon aria-hidden className="h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
            <p className="truncate text-[0.625rem] font-medium leading-[0.75rem] tracking-wide sm:text-xs">
              {trimmed}
            </p>
          </div>
        ) : (
          <p className="text-sm font-medium leading-5 text-muted-foreground">
            {emptyLabel}
          </p>
        )}
      </div>
    );
  },
);
